import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { AffiliateProgram } from '../services/api';

interface AffiliateProgramViewModalProps {
  visible: boolean;
  program: AffiliateProgram | null;
  onCancel: () => void;
}

const AffiliateProgramViewModal: React.FC<AffiliateProgramViewModalProps> = ({
  visible,
  program,
  onCancel,
}) => {
  if (!visible || !program) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCommission = (type: string, value: number) => {
    if (type === 'percentage') {
      return `${value}%`;
    }
    return formatCurrency(value);
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Affiliate Program Details</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">ID</label>
                <p className="text-sm text-gray-900 mt-1">{program.id}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <p className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    program.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {program.isActive ? 'Active' : 'Inactive'}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Program Details */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Program Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Name</label>
                <p className="text-sm text-gray-900 mt-1 font-semibold">{program.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Commission</label>
                <p className="text-sm text-gray-900 mt-1 font-semibold">
                  {formatCommission(program.commissionType, Number(program.commissionValue))}
                </p>
                <p className="text-xs text-gray-500 mt-1">{program.commissionType}</p>
              </div>
            </div>
          </div>

          {/* Period */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Period</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Start Date</label>
                <p className="text-sm text-gray-900 mt-1">{formatDate(program.startDate)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">End Date</label>
                <p className="text-sm text-gray-900 mt-1">{formatDate(program.endDate)}</p>
              </div>
            </div>
          </div>

          {/* Products */}
          {program.products && program.products.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Products ({program.products.length})
              </h3>
              <div className="space-y-2">
                {program.products.map((product) => (
                  <div key={product.id} className="bg-gray-50 p-3 rounded-md">
                    <div className="text-sm font-medium text-gray-900">
                      {product.product?.name || `Product ID: ${product.productId}`}
                    </div>
                    {product.product?.slug && (
                      <div className="text-xs text-gray-500 mt-1">{product.product.slug}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dates */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Dates</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Created At</label>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(program.createdAt).toLocaleString('id-ID')}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Updated At</label>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(program.updatedAt).toLocaleString('id-ID')}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-imola-primary text-white rounded-md hover:bg-imola-primary/80"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateProgramViewModal;

